/**
 * Générateur — Produire et exécuter une séquence d'instructions
 * Attendu ID : 688 | Niveau : 6ème | Domaine : Algorithmique (initiation à la programmation)
 *
 * Types générés (1 catégorie, 5 formats) :
 *   - arrivee_qcm             : QCM 4 choix — exécuter : sur quelle case marquée termine le robot ?
 *   - arrivee_saisie          : saisie — exécuter : nommer la case d'arrivée (grille à coordonnées, ex. « C4 »)
 *   - chemin_drag             : drag — produire : compléter la séquence du chemin tracé (flèches à glisser)
 *   - instruction_manquante_qcm : QCM 4 choix — produire : retrouver l'instruction effacée du programme
 *   - executer_vf             : V/F 4 lignes — exécuter : affirmations sur le déroulement du programme
 *
 * Langage commun aux gens 687-690 (eduscol cycle 3, débranché) : déplacements
 * ABSOLUS sur grille 5×5, une instruction = une case, flèches ↑ ↓ ← →, robot
 * sur la case « D ». Coordonnées type bataille navale quand une case doit être
 * nommée : lignes A-E (de haut en bas), colonnes 1-5 (de gauche à droite).
 *
 * Frontière d'attendu : on EXÉCUTE une séquence donnée ou on la PRODUIT
 * (drag, instruction manquante) — jamais de reconnaissance parmi des
 * propositions toutes faites (687), pas de boucle (689), pas de construction
 * libre d'un chemin sous contraintes (690).
 *
 * Invariants par construction :
 *   • chemins toujours dans la grille (tirage avec retentatives) ;
 *   • QCM arrivée : cases leurres = arrivées de programmes mutés, toutes
 *     distinctes entre elles, de la bonne et du départ ;
 *   • instruction manquante : réponse unique par bilan des déplacements
 *     (une seule flèche comble l'écart départ → cible) ;
 *   • drag : le chemin tracé impose l'ordre — solution positionnelle unique ;
 *   • V/F : clés sémantiques distinctes, quota variable, phrasé positif ou
 *     négatif choisi pour incarner le camp demandé sans double négation.
 *
 * Sources d'inspiration (recherche 2026-08-11) : Sésamath ex. 5 (position
 * finale), eduscol « construire un codage », Daval ch. 13 (coder/exécuter).
 */

import {
    randomChoice, randInt, shuffle,
    makeQcm, makeSaisie, makeVraiFaux, makeTexteTrou,
} from '../../../utils';

// ─── Constantes ──────────────────────────────────────────────
const ATTENDU_ID = 688;

const L_ARRQ = 'Exécuter un programme : la case d\'arrivée (QCM)';
const L_ARRS = 'Exécuter un programme : nommer la case d\'arrivée (saisie)';
const L_DRAG = 'Produire la séquence d\'un chemin tracé (glisser-déposer)';
const L_MANQ = 'Retrouver l\'instruction manquante (QCM)';
const L_VF   = 'Dérouler un programme (V/F)';

const opts = (label) => ({ niveau: '6eme', matiere: 'maths', attendu: ATTENDU_ID, label });

// ─── Moteur de déplacements (partagé avec le gen 687) ────────

const TAILLE = 5;

const DIRS = {
    '↑': { dx: 0, dy: -1, nom: 'monter d\'une case' },
    '↓': { dx: 0, dy: 1, nom: 'descendre d\'une case' },
    '←': { dx: -1, dy: 0, nom: 'aller d\'une case vers la gauche' },
    '→': { dx: 1, dy: 0, nom: 'aller d\'une case vers la droite' },
};
const FLECHES = Object.keys(DIRS);
const OPPOSE = { '↑': '↓', '↓': '↑', '←': '→', '→': '←' };

const LIGNES_NOMS = ['A', 'B', 'C', 'D', 'E'];
const nomCase = (c) => `${LIGNES_NOMS[c.y]}${c.x + 1}`;

const executer = (depart, prog) => {
    const cells = [{ ...depart }];
    let { x, y } = depart;
    for (const f of prog) {
        x += DIRS[f].dx;
        y += DIRS[f].dy;
        if (x < 0 || y < 0 || x >= TAILLE || y >= TAILLE) return null;
        cells.push({ x, y });
    }
    return cells;
};

const cleChemin = (cells) => cells.map(c => `${c.x},${c.y}`).join('|');

const estAutoEvitant = (cells) =>
    cells !== null && new Set(cells.map(c => `${c.x},${c.y}`)).size === cells.length;

/** Chemin auto-évitant (pour les formats où le chemin est dessiné). */
const tirerCheminAutoEvitant = (longueur) => {
    for (let essai = 0; essai < 200; essai++) {
        const depart = { x: randInt(0, TAILLE - 1), y: randInt(0, TAILLE - 1) };
        const prog = [];
        for (let i = 0; i < longueur; i++) {
            const interdites = i > 0 ? [OPPOSE[prog[i - 1]]] : [];
            prog.push(randomChoice(FLECHES.filter(f => !interdites.includes(f))));
        }
        const cells = executer(depart, prog);
        if (estAutoEvitant(cells)) return { depart, prog, cells };
    }
    const depart = { x: 0, y: TAILLE - 1 };
    const prog = ['→', '↑', '→', '↑'].slice(0, longueur);
    return { depart, prog, cells: executer(depart, prog) };
};

/**
 * Marche libre dans la grille (croisements autorisés — formats d'exécution
 * mentale). L'arrivée est garantie différente du départ : sinon la marque
 * d'arrivée recouvrirait le « D » sur la grille.
 */
const tirerCheminLibre = (longueur) => {
    for (let essai = 0; essai < 200; essai++) {
        const depart = { x: randInt(0, TAILLE - 1), y: randInt(0, TAILLE - 1) };
        const prog = [];
        for (let i = 0; i < longueur; i++) prog.push(randomChoice(FLECHES));
        const cells = executer(depart, prog);
        if (cells === null) continue;
        const arrivee = cells[cells.length - 1];
        if (arrivee.x === depart.x && arrivee.y === depart.y) continue;
        return { depart, prog, cells };
    }
    return tirerCheminAutoEvitant(longueur);
};

// ─── Mutations (leurres d'arrivée) ───────────────────────────

const muterProg = (prog) => {
    const copie = [...prog];
    const i = randInt(0, prog.length - 1);
    copie[i] = randomChoice(FLECHES.filter(f => f !== copie[i]));
    return copie;
};

/**
 * `nb` cases d'arrivée leurres, toutes distinctes (≠ bonne, ≠ départ).
 * Trois familles d'erreurs simulées (revue 2026-08-12 : la mutation simple
 * seule plaçait TOUS les leurres à distance 2 de l'arrivée — la bonne était
 * repérable comme « centre » du nuage dans 32 % des tirages) :
 *   - mutation d'une instruction (distance 2) ;
 *   - oubli de la dernière instruction (programme tronqué, distance 1) ;
 *   - mutation de deux instructions (distance jusqu'à 4).
 */
const manhattan = (a, b) => Math.abs(a.x - b.x) + Math.abs(a.y - b.y);

/**
 * Garde anti « centre du nuage » (revue 2026-08-12) : les leurres étant des
 * erreurs simulées AUTOUR de la bonne, celle-ci était repérable sans rien
 * exécuter comme l'unique case centrale des 4 marques (37 % des tirages).
 * On rejette tout jeu où la bonne minimise STRICTEMENT le max ou la somme
 * des distances aux trois autres.
 */
const bonneEstCentreStrict = (bonne, leurres) => {
    const cells = [bonne, ...leurres];
    const scoresMax = cells.map(c => Math.max(...cells.map(o => manhattan(c, o))));
    const scoresSomme = cells.map(c => cells.reduce((s, o) => s + manhattan(c, o), 0));
    const centreMax = scoresMax[0] === Math.min(...scoresMax)
        && scoresMax.filter(s => s === scoresMax[0]).length === 1;
    const centreSomme = scoresSomme[0] === Math.min(...scoresSomme)
        && scoresSomme.filter(s => s === scoresSomme[0]).length === 1;
    return centreMax || centreSomme;
};

const tirerArriveesLeurres = (depart, prog, nb) => {
    const arriveeBonne = executer(depart, prog).slice(-1)[0];
    for (let tentative = 0; tentative < 40; tentative++) {
        const vues = new Set([`${arriveeBonne.x},${arriveeBonne.y}`, `${depart.x},${depart.y}`]);
        const leurres = [];
        for (let essai = 0; essai < 200 && leurres.length < nb; essai++) {
            const famille = essai % 3;
            let candidat;
            if (famille === 0) candidat = muterProg(prog);
            else if (famille === 1) candidat = prog.slice(0, prog.length - 1);
            else candidat = muterProg(muterProg(prog));
            const cells = executer(depart, candidat);
            if (cells === null) continue;
            const arr = cells[cells.length - 1];
            const cle = `${arr.x},${arr.y}`;
            if (vues.has(cle)) continue;
            vues.add(cle);
            leurres.push(arr);
        }
        if (leurres.length !== nb) continue;
        if (bonneEstCentreStrict(arriveeBonne, leurres)) continue;
        return leurres;
    }
    return null;
};

// ─── Rendu SVG ───────────────────────────────────────────────

const CASE = 26;
const BLEU = '#1E3A8A';
const VERT = '#1D9E75';
const ORANGE = '#B45309';

const svgToElement = (svg) => ({
    version: 1,
    elements: [{ type: 'image', config: { imageUrl: `data:image/svg+xml,${encodeURIComponent(svg)}`, maxWidth: 'compact' } }],
});

const enteteSvg = (w, h, aria) =>
    `<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 ${w} ${h}" role="img" aria-label="${aria}">`;

/**
 * Grille 5×5. options : { axes, chemin (cells à tracer), marques ([{cell,
 * label}] cases cerclées orange), cible (case étoilée orange) }.
 * Le départ (1re case de `cells`) est toujours marqué « D ».
 */
const svgGrille = (ox, oy, cells, options = {}) => {
    const w = TAILLE * CASE;
    const parts = [];
    for (let i = 0; i <= TAILLE; i++) {
        parts.push(`<line x1="${ox + i * CASE}" y1="${oy}" x2="${ox + i * CASE}" y2="${oy + w}" stroke="#cbd5e1" stroke-width="1"/>`);
        parts.push(`<line x1="${ox}" y1="${oy + i * CASE}" x2="${ox + w}" y2="${oy + i * CASE}" stroke="#cbd5e1" stroke-width="1"/>`);
    }
    parts.push(`<rect x="${ox}" y="${oy}" width="${w}" height="${w}" fill="none" stroke="${BLEU}" stroke-width="1.4"/>`);
    if (options.axes) {
        for (let i = 0; i < TAILLE; i++) {
            parts.push(`<text x="${ox - 9}" y="${oy + i * CASE + CASE / 2 + 4}" font-size="11" font-weight="600" fill="${BLEU}" text-anchor="middle" font-family="system-ui,sans-serif">${LIGNES_NOMS[i]}</text>`);
            parts.push(`<text x="${ox + i * CASE + CASE / 2}" y="${oy - 6}" font-size="11" font-weight="600" fill="${BLEU}" text-anchor="middle" font-family="system-ui,sans-serif">${i + 1}</text>`);
        }
    }
    const centre = (c) => ({ cx: ox + c.x * CASE + CASE / 2, cy: oy + c.y * CASE + CASE / 2 });
    if (options.chemin) {
        const pts = options.chemin.map(c => { const p = centre(c); return `${p.cx},${p.cy}`; }).join(' ');
        const fin = centre(options.chemin[options.chemin.length - 1]);
        const avant = centre(options.chemin[options.chemin.length - 2]);
        const vx = Math.sign(fin.cx - avant.cx), vy = Math.sign(fin.cy - avant.cy);
        parts.push(`<polyline points="${pts}" fill="none" stroke="${VERT}" stroke-width="2.4"/>`);
        parts.push(`<path d="M${fin.cx + vx * 6},${fin.cy + vy * 6} L${fin.cx - vy * 4 - vx * 2},${fin.cy - vx * 4 - vy * 2} L${fin.cx + vy * 4 - vx * 2},${fin.cy + vx * 4 - vy * 2} Z" fill="${VERT}"/>`);
    }
    (options.marques || []).forEach(({ cell, label }) => {
        const p = centre(cell);
        parts.push(`<circle cx="${p.cx}" cy="${p.cy}" r="9" fill="none" stroke="${ORANGE}" stroke-width="1.8"/>`);
        parts.push(`<text x="${p.cx}" y="${p.cy + 3.5}" font-size="10" font-weight="700" fill="${ORANGE}" text-anchor="middle" font-family="system-ui,sans-serif">${label}</text>`);
    });
    if (options.cible) {
        const p = centre(options.cible);
        parts.push(`<rect x="${p.cx - 9}" y="${p.cy - 9}" width="18" height="18" rx="3" fill="${ORANGE}" opacity="0.25"/>`);
        parts.push(`<circle cx="${p.cx}" cy="${p.cy}" r="4" fill="${ORANGE}"/>`);
    }
    const dep = centre(cells[0]);
    parts.push(`<circle cx="${dep.cx}" cy="${dep.cy}" r="8" fill="${BLEU}"/>`);
    parts.push(`<text x="${dep.cx}" y="${dep.cy + 3.5}" font-size="10" font-weight="700" fill="#ffffff" text-anchor="middle" font-family="system-ui,sans-serif">D</text>`);
    return parts.join('');
};

const svgProgramme = (ox, oy, prog, posMasquee = -1) => {
    return prog.map((f, i) => {
        const x = ox + i * 30;
        const masque = i === posMasquee;
        const fond = masque ? '#FEF3C7' : '#EFF6FF';
        const bord = masque ? ORANGE : BLEU;
        const contenu = masque ? '?' : f;
        return `<rect x="${x}" y="${oy}" width="22" height="22" rx="4" fill="${fond}" stroke="${bord}" stroke-width="1.2"/>` +
            `<text x="${x + 11}" y="${oy + 16}" font-size="14" font-weight="700" fill="${masque ? ORANGE : BLEU}" text-anchor="middle" font-family="system-ui,sans-serif">${contenu}</text>` +
            `<text x="${x + 11}" y="${oy + 36}" font-size="9" fill="#64748b" text-anchor="middle" font-family="system-ui,sans-serif">${i + 1}</text>`;
    }).join('');
};

const progEnTexte = (prog) => prog.join(' ');

// Signature d'anti-répétition (revue 2026-08-12, recette gen 687) : le texte
// de la plupart de ces formats est fixe, le contenu variable vit dans le SVG.
const signatureDe = (payload) =>
    (payload.elements_additionnels?.elements?.[0]?.config?.imageUrl || '') + payload.texte;

// ─── Format 1 — Case d'arrivée (QCM) ─────────────────────────

let derniereSignatureArrQ = null;

const genArriveeQcm = () => {
    let payload = construireArriveeQcm();
    for (let t = 0; t < 8 && signatureDe(payload) === derniereSignatureArrQ; t++) {
        payload = construireArriveeQcm();
    }
    derniereSignatureArrQ = signatureDe(payload);
    return payload;
};

const construireArriveeQcm = () => {
    let tirage, leurres;
    do {
        tirage = tirerCheminLibre(randInt(4, 6));
        leurres = tirerArriveesLeurres(tirage.depart, tirage.prog, 3);
    } while (leurres === null);

    const arrivee = tirage.cells[tirage.cells.length - 1];
    const lettres = ['A', 'B', 'C', 'D'];
    const casesMarquees = shuffle([arrivee, ...leurres]);
    const marques = casesMarquees.map((cell, i) => ({ cell, label: lettres[i] }));
    const bonne = `Case ${lettres[casesMarquees.indexOf(arrivee)]}`;

    const largeurGrille = TAILLE * CASE;
    const progLargeur = tirage.prog.length * 30 - 8;
    const w = Math.max(largeurGrille + 20, progLargeur + 20);
    const svg = enteteSvg(w, largeurGrille + 80,
        'Un programme numéroté en blocs flèches, et une grille avec la case départ D et quatre cases marquées A, B, C et D')
        + svgProgramme((w - progLargeur) / 2, 10, tirage.prog)
        + svgGrille((w - largeurGrille) / 2, 58, tirage.cells, { marques })
        + '</svg>';

    const texte = `<p>Le robot part de la case marquée « D » et exécute le programme ci-dessus. Chaque flèche le déplace d'une case. Sur quelle case marquée termine-t-il ?</p>`;

    const payload = makeQcm(lettres.map(l => `Case ${l}`), bonne, texte, opts(L_ARRQ));
    payload.elements_additionnels = svgToElement(svg);
    payload._meta.generatorKey = 'arrivee_qcm';
    return payload;
};

// ─── Format 2 — Case d'arrivée (saisie) ──────────────────────

let derniereSignatureArrS = null;

const genArriveeSaisie = () => {
    let payload = construireArriveeSaisie();
    for (let t = 0; t < 8 && signatureDe(payload) === derniereSignatureArrS; t++) {
        payload = construireArriveeSaisie();
    }
    derniereSignatureArrS = signatureDe(payload);
    return payload;
};

const construireArriveeSaisie = () => {
    const tirage = tirerCheminLibre(randInt(4, 6));
    const arrivee = tirage.cells[tirage.cells.length - 1];

    const largeurGrille = TAILLE * CASE;
    const progLargeur = tirage.prog.length * 30 - 8;
    const w = Math.max(largeurGrille + 40, progLargeur + 20);
    const svg = enteteSvg(w, largeurGrille + 90,
        'Un programme numéroté en blocs flèches, et une grille à coordonnées : lignes A à E, colonnes 1 à 5, avec la case départ D')
        + svgProgramme((w - progLargeur) / 2, 10, tirage.prog)
        + svgGrille((w - largeurGrille) / 2 + 8, 68, tirage.cells, { axes: true })
        + '</svg>';

    const texte = `<p>Le robot part de la case marquée « D » et exécute le programme ci-dessus. Chaque flèche le déplace d'une case. Sur quelle case termine-t-il ? Écris le nom de la case, comme « ${nomCase({ x: 2, y: 1 })} » (ligne puis colonne).</p>`;

    const payload = makeSaisie(nomCase(arrivee), texte, {
        ...opts(L_ARRS),
        strict: false,
    });
    payload.elements_additionnels = svgToElement(svg);
    payload._meta.generatorKey = 'arrivee_saisie';
    return payload;
};

// ─── Format 3 — Compléter la séquence du chemin (drag) ───────

let derniereSignatureDrag = null;

const genCheminDrag = () => {
    let payload = construireCheminDrag();
    for (let t = 0; t < 8 && signatureDe(payload) === derniereSignatureDrag; t++) {
        payload = construireCheminDrag();
    }
    derniereSignatureDrag = signatureDe(payload);
    return payload;
};

const construireCheminDrag = () => {
    const tirage = tirerCheminAutoEvitant(randInt(5, 6));
    const nbTrous = randInt(3, 4);
    const positionsTrous = new Set(shuffle([...Array(tirage.prog.length).keys()]).slice(0, nbTrous));

    const phrase = tirage.prog
        .map((f, i) => (positionsTrous.has(i) ? `{{${f}}}` : f))
        .join(' ');
    // Deux flèches en trop dans le stock (peuvent doubler des flèches utiles).
    const distracteurs = [randomChoice(FLECHES), randomChoice(FLECHES)];

    const largeurGrille = TAILLE * CASE;
    const svg = enteteSvg(largeurGrille + 20, largeurGrille + 20,
        'Une grille avec la case départ D et un chemin tracé, terminé par une pointe de flèche')
        + svgGrille(10, 10, tirage.cells, { chemin: tirage.cells })
        + '</svg>';

    const texte = `<p>Le robot part de la case marquée « D » et suit le chemin tracé ci-dessus. Chaque flèche le déplace d'une case. Complète la séquence d'instructions de ce chemin en plaçant les flèches manquantes (attention, certaines flèches proposées sont en trop).</p>`;

    const payload = makeTexteTrou(phrase, texte, {
        ...opts(L_DRAG),
        mode: 'drag',
        distracteurs,
    });
    payload.elements_additionnels = svgToElement(svg);
    payload._meta.generatorKey = 'chemin_drag';
    return payload;
};

// ─── Format 4 — Instruction manquante (QCM) ──────────────────

let derniereSignatureManq = null;

const genInstructionManquanteQcm = () => {
    let payload = construireInstructionManquanteQcm();
    for (let t = 0; t < 8 && signatureDe(payload) === derniereSignatureManq; t++) {
        payload = construireInstructionManquanteQcm();
    }
    derniereSignatureManq = signatureDe(payload);
    return payload;
};

const construireInstructionManquanteQcm = () => {
    const tirage = tirerCheminLibre(randInt(4, 5));
    const arrivee = tirage.cells[tirage.cells.length - 1];
    const posMasquee = randInt(0, tirage.prog.length - 1);

    const largeurGrille = TAILLE * CASE;
    const progLargeur = tirage.prog.length * 30 - 8;
    const w = Math.max(largeurGrille + 20, progLargeur + 20);
    const svg = enteteSvg(w, largeurGrille + 80,
        'Un programme numéroté dont une instruction est remplacée par un point d\'interrogation, et une grille avec la case départ D et la case cible marquée en orange')
        + svgProgramme((w - progLargeur) / 2, 10, tirage.prog, posMasquee)
        + svgGrille((w - largeurGrille) / 2, 58, tirage.cells, { cible: arrivee })
        + '</svg>';

    const texte = `<p>Le robot part de la case marquée « D » et doit terminer sur la case orange. Une instruction du programme ci-dessus a été effacée (le bloc « ? »). Quelle est l'instruction manquante ?</p>`;

    const payload = makeQcm([...FLECHES], tirage.prog[posMasquee], texte, opts(L_MANQ));
    payload.elements_additionnels = svgToElement(svg);
    payload._meta.generatorKey = 'instruction_manquante_qcm';
    return payload;
};

// ─── Format 5 — Dérouler un programme (V/F) ──────────────────

let derniereSignatureVF688 = null;

const genExecuterVf = () => {
    let payload = construireExecuterVf();
    for (let t = 0; t < 8 && signatureDe(payload) === derniereSignatureVF688; t++) {
        payload = construireExecuterVf();
    }
    derniereSignatureVF688 = signatureDe(payload);
    return payload;
};

// Chaque builder retourne { l, v, cases (cases nommées dans la ligne) } ou
// null. `vrai` = camp demandé. `g` = groupe d'exclusion (revue 2026-08-12) :
// deux lignes d'un même groupe se déduisent l'une de l'autre (« termine sur
// C4 » donne « même ligne que le départ » ; « N cases différentes » + la
// longueur affichée du programme donne le repassage) — une seule par groupe.
const BUILDERS_VF_688 = [
    {
        k: 'arrivee',
        g: 'arrivee',
        b: (tirage, vrai) => {
            const arrivee = tirage.cells[tirage.cells.length - 1];
            if (vrai) return { l: `Le robot termine sur la case ${nomCase(arrivee)}.`, v: true, cases: [nomCase(arrivee)] };
            const autres = [];
            for (let x = 0; x < TAILLE; x++) {
                for (let y = 0; y < TAILLE; y++) {
                    if (x !== arrivee.x || y !== arrivee.y) autres.push({ x, y });
                }
            }
            const c = randomChoice(autres);
            return { l: `Le robot termine sur la case ${nomCase(c)}.`, v: false, cases: [nomCase(c)] };
        },
    },
    {
        k: 'meme_ligne',
        g: 'arrivee',
        b: (tirage, vrai) => {
            const memeLigne = tirage.cells[tirage.cells.length - 1].y === tirage.cells[0].y;
            const l = (memeLigne === vrai)
                ? 'Le robot termine sur la même ligne que sa case de départ.'
                : 'Le robot termine sur une ligne différente de celle de sa case de départ.';
            return { l, v: vrai, cases: [] };
        },
    },
    {
        k: 'etape_k',
        g: 'etape',
        b: (tirage, vrai) => {
            const k = randInt(1, tirage.prog.length - 1);
            const reelle = tirage.cells[k];
            if (vrai) return { l: `Après l'instruction n°${k}, le robot est sur la case ${nomCase(reelle)}.`, v: true, cases: [nomCase(reelle)] };
            const autres = [];
            for (let x = 0; x < TAILLE; x++) {
                for (let y = 0; y < TAILLE; y++) {
                    if (x !== reelle.x || y !== reelle.y) autres.push({ x, y });
                }
            }
            const c = randomChoice(autres);
            return { l: `Après l'instruction n°${k}, le robot est sur la case ${nomCase(c)}.`, v: false, cases: [nomCase(c)] };
        },
    },
    {
        k: 'passe_par',
        g: 'passage',
        b: (tirage, vrai) => {
            const surChemin = new Set(tirage.cells.map(c => `${c.x},${c.y}`));
            const candidates = [];
            for (let x = 0; x < TAILLE; x++) {
                for (let y = 0; y < TAILLE; y++) {
                    const interne = surChemin.has(`${x},${y}`);
                    const dep = tirage.cells[0];
                    const arr = tirage.cells[tirage.cells.length - 1];
                    const estExtremite = (x === dep.x && y === dep.y) || (x === arr.x && y === arr.y);
                    if (vrai && interne && !estExtremite) candidates.push({ x, y });
                    if (!vrai && !interne) candidates.push({ x, y });
                }
            }
            if (!candidates.length) return null;
            const c = randomChoice(candidates);
            return { l: `Au cours de son trajet, le robot passe par la case ${nomCase(c)}.`, v: vrai, cases: [nomCase(c)] };
        },
    },
    {
        k: 'revisite',
        g: 'trace',
        b: (tirage, vrai) => {
            const repasse = !estAutoEvitant(tirage.cells);
            const l = (repasse === vrai)
                ? 'Pendant son trajet, le robot passe deux fois par une même case.'
                : 'Pendant son trajet, le robot ne repasse jamais par une case déjà visitée.';
            return { l, v: vrai, cases: [] };
        },
    },
    {
        k: 'nb_cases',
        g: 'trace',
        b: (tirage, vrai) => {
            const reel = new Set(tirage.cells.map(c => `${c.x},${c.y}`)).size;
            const n = vrai ? reel : reel + randomChoice([-1, 1]);
            if (n < 1) return null;
            return { l: `En tout, le robot passe par ${n} cases différentes (départ compris).`, v: vrai, cases: [] };
        },
    },
];

const construireExecuterVf = () => {
    const tirage = tirerCheminLibre(randInt(4, 6));

    const largeurGrille = TAILLE * CASE;
    const progLargeur = tirage.prog.length * 30 - 8;
    const w = Math.max(largeurGrille + 40, progLargeur + 20);
    const svg = enteteSvg(w, largeurGrille + 90,
        'Un programme numéroté en blocs flèches, et une grille à coordonnées lignes A à E, colonnes 1 à 5, avec la case départ D')
        + svgProgramme((w - progLargeur) / 2, 10, tirage.prog)
        + svgGrille((w - largeurGrille) / 2 + 8, 68, tirage.cells, { axes: true })
        + '</svg>';

    const texte = `<p>Le robot part de la case marquée « D » et exécute le programme ci-dessus. Chaque flèche le déplace d'une case. Pour chaque affirmation, indique si elle est vraie ou fausse.</p>`;

    for (let essai = 0; essai < 60; essai++) {
        const nbV = randInt(1, 3);
        const camps = shuffle([...Array(4)].map((_, i) => i < nbV));
        // Un builder par groupe d'exclusion (revue 2026-08-12).
        const parGroupe = {};
        shuffle([...BUILDERS_VF_688]).forEach((bd) => {
            if (!parGroupe[bd.g]) parGroupe[bd.g] = bd;
        });
        const builders = shuffle(Object.values(parGroupe));
        if (builders.length !== 4) continue;
        const items = builders.map((bd, i) => bd.b(tirage, camps[i]));
        if (items.some(x => x === null)) continue;
        // Cases nommées deux à deux distinctes entre les lignes.
        const casesNommees = items.flatMap(x => x.cases);
        if (new Set(casesNommees).size !== casesNommees.length) continue;
        const lignes = items.map(x => x.l);
        if (new Set(lignes).size !== 4) continue;
        const bonnesReponses = items.map(x => (x.v ? 0 : 1));
        const payload = makeVraiFaux(lignes, bonnesReponses, texte, {
            ...opts(L_VF),
            elements_additionnels: svgToElement(svg),
        });
        payload._meta.generatorKey = 'executer_vf';
        return payload;
    }
    return construireExecuterVf();
};

// ─── Registry & Export ───────────────────────────────────────

const GENERATORS = {
    arrivee_qcm:               genArriveeQcm,
    arrivee_saisie:            genArriveeSaisie,
    chemin_drag:               genCheminDrag,
    instruction_manquante_qcm: genInstructionManquanteQcm,
    executer_vf:               genExecuterVf,
};

export const CATEGORIES = [
    {
        id: 'produire_executer_sequences',
        label: 'Produire et exécuter une séquence d\'instructions',
        formatsList: [
            { key: 'arrivee_qcm',               label: L_ARRQ },
            { key: 'arrivee_saisie',            label: L_ARRS },
            { key: 'chemin_drag',               label: L_DRAG },
            { key: 'instruction_manquante_qcm', label: L_MANQ },
            { key: 'executer_vf',               label: L_VF },
        ],
    },
];

export const generate = (typeExercice) => {
    let key;
    if (typeExercice && GENERATORS[typeExercice]) {
        key = typeExercice;
    } else if (typeExercice) {
        const matching = Object.keys(GENERATORS).filter(
            (k) => k.startsWith(typeExercice) || k.includes(typeExercice),
        );
        key = matching.length ? randomChoice(matching) : randomChoice(Object.keys(GENERATORS));
    } else {
        key = randomChoice(Object.keys(GENERATORS));
    }
    const payload = GENERATORS[key]();
    if (!payload._meta.generatorKey) payload._meta.generatorKey = key;
    return payload;
};

export const _internals = {
    TAILLE,
    DIRS,
    executer,
    nomCase,
    tirerCheminAutoEvitant,
    tirerCheminLibre,
    tirerArriveesLeurres,
    BUILDERS_VF_688,
    genArriveeQcm,
    genArriveeSaisie,
    genCheminDrag,
    genInstructionManquanteQcm,
    genExecuterVf,
};
